import React, { useState } from 'react';
import './SetaDocs.css'; // Assuming you named the dark theme file this based on class names
import { auth } from '../../backend/firebase';

const SetaDocs = () => {
  const [activeTab, setActiveTab] = useState('node');
  const [copiedIndex, setCopiedIndex] = useState(null);

  // SAFE UID ACCESS:
  // If auth.currentUser is null (loading or not logged in), use a placeholder to prevent crashes.
  const userUid = auth.currentUser?.uid || "YOUR_USER_ID_HERE";

  const handleCopy = (code, index) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const getCodeContent = () => {
    if (activeTab === 'node') {
      return {
        install: 'npm install @seta-dot/datasets',
        // Method 1: getRecentSeta
        usageRecent: `const { getRecentSeta } = require('@seta-dot/datasets');

// Fetch the absolute latest version of the dataset
getRecentSeta({
  id: "08172608-2599-4ef3-ba09-7910a9d9e17b",
  userId: "${userUid}",
  savePath: "./cricket-data"
});`,
        // Method 2: getSetaByVersion
        usageVersion: `const { getSetaByVersion } = require('@seta-dot/datasets');

// Fetch a specific immutable version
getSetaByVersion({
  id: "08172608-2599-4ef3-ba09-7910a9d9e17b",
  version: "1.0.0",
  userId: "${userUid}",
  savePath: "./cricket-data/v1"
});`,
        // Method 3: getSetaInstance
        usageInstance: `const { getSetaInstance } = require('@seta-dot/datasets');

// Fetch a specific run/instance of a version
getSetaInstance({
  id: "08172608-2599-4ef3-ba09-7910a9d9e17b",
  version: "1.0.0",
  instanceId: "so02UdjOQD2dKcHdh2XZ",
  userId: "${userUid}",
  savePath: "./cricket-data/run-55"
});`
      };
    } else {
      // PYTHON
      return {
        install: 'pip install seta-datasets',
        // Method 1: get_recent_seta
        usageRecent: `from seta import get_recent_seta

# Fetch the absolute latest version of the dataset
get_recent_seta(
    dataset_id="08172608-2599-4ef3-ba09-7910a9d9e17b",
    user_id="${userUid}",
    save_path="./cricket-data"
)`,
        // Method 2: get_seta_by_version
        usageVersion: `from seta import get_seta_by_version

# Fetch a specific immutable version
get_seta_by_version(
    dataset_id="08172608-2599-4ef3-ba09-7910a9d9e17b",
    version="1.0.0",
    user_id="${userUid}",
    save_path="./cricket-data/v1"
)`,
        // Method 3: get_seta_instance
        usageInstance: `from seta import get_seta_instance

# Fetch a specific run/instance of a version
get_seta_instance(
    dataset_id="08172608-2599-4ef3-ba09-7910a9d9e17b",
    version="1.0.0",
    instance_id="so02UdjOQD2dKcHdh2XZ",
    user_id="${userUid}",
    save_path="./cricket-data/run-55"
)`
      };
    }
  };

  const content = getCodeContent();

  return (
    <div className="sd-d-wrapper">
      {/* --- HERO SECTION --- */}
      <div className="sd-d-hero">
        <h1 className="sd-d-hero-title">SETA Datasets</h1>
        <p className="sd-d-hero-subtitle">
          The official {activeTab === 'node' ? 'Node.js' : 'Python'} client for the SETA Protocol. 
          Fetch, validate, and download datasets with enterprise-grade reliability.
        </p>
        <div className="sd-d-badges-row">
          <span className="sd-d-status-badge">v2.1.0</span>
          <span className="sd-d-status-badge">MIT License</span>
          <span className="sd-d-status-badge">Production Ready</span>
        </div>
      </div>

      <div className="sd-d-container">
        {/* --- TABS --- */}
        <div className="sd-d-tab-nav">
          <button 
            className={`sd-d-tab-btn ${activeTab === 'node' ? 'sd-d-active' : ''}`}
            onClick={() => setActiveTab('node')}
          >
            Node.js
          </button>
          <button 
            className={`sd-d-tab-btn ${activeTab === 'python' ? 'sd-d-active' : ''}`}
            onClick={() => setActiveTab('python')}
          >
            Python
          </button>
        </div>

        {/* --- INSTALLATION --- */}
        <div className="sd-d-content-card">
          <h2 className="sd-d-card-heading">Installation</h2>
          <p className="sd-d-card-desc">Add the package to your project using your preferred package manager.</p>
          
          <UniqueCodeBlock 
            lang="BASH" 
            code={content.install} 
            onCopy={() => handleCopy(content.install, 0)}
            isCopied={copiedIndex === 0}
          />
        </div>

        {/* --- METHOD 1: RECENT --- */}
        <div className="sd-d-content-card">
          <h2 className="sd-d-card-heading">
            {activeTab === 'node' ? '1. getRecentSeta' : '1. get_recent_seta'}
          </h2>
          <p className="sd-d-card-desc">
            Use this method to download the <strong>latest</strong> available version of a dataset.
          </p>

          <UniqueCodeBlock 
            lang={activeTab === 'node' ? 'JAVASCRIPT' : 'PYTHON'} 
            code={content.usageRecent} 
            onCopy={() => handleCopy(content.usageRecent, 1)}
            isCopied={copiedIndex === 1}
          />

          <UniqueParameterTable 
            method="recent"
            lang={activeTab}
          />
        </div>

        {/* --- METHOD 2: VERSION --- */}
        <div className="sd-d-content-card">
          <h2 className="sd-d-card-heading">
            {activeTab === 'node' ? '2. getSetaByVersion' : '2. get_seta_by_version'}
          </h2>
          <p className="sd-d-card-desc">
            Use this method when you need reproducibility by locking your download to a specific semantic version tag.
          </p>

          <UniqueCodeBlock 
            lang={activeTab === 'node' ? 'JAVASCRIPT' : 'PYTHON'} 
            code={content.usageVersion} 
            onCopy={() => handleCopy(content.usageVersion, 2)}
            isCopied={copiedIndex === 2}
          />

           <UniqueParameterTable 
            method="version"
            lang={activeTab}
          />
        </div>

        {/* --- METHOD 3: INSTANCE --- */}
        <div className="sd-d-content-card">
          <h2 className="sd-d-card-heading">
            {activeTab === 'node' ? '3. getSetaInstance' : '3. get_seta_instance'}
          </h2>
          <p className="sd-d-card-desc">
            Use this method to retrieve a specific instance (run) of a version. This is useful for auditing specific data pipelines.
          </p>

          <UniqueCodeBlock 
            lang={activeTab === 'node' ? 'JAVASCRIPT' : 'PYTHON'} 
            code={content.usageInstance} 
            onCopy={() => handleCopy(content.usageInstance, 3)}
            isCopied={copiedIndex === 3}
          />

           <UniqueParameterTable 
            method="instance"
            lang={activeTab}
          />
        </div>
      </div>
    </div>
  );
};

// --- HELPER COMPONENTS (With Unique Classnames) ---

const UniqueCodeBlock = ({ lang, code, onCopy, isCopied }) => (
  <div className="sd-d-code-frame">
    <div className="sd-d-code-header">
      <span className="sd-d-lang-label">{lang}</span>
      <button className="sd-d-copy-btn" onClick={onCopy}>
        {isCopied ? 'Copied!' : 'Copy Code'}
      </button>
    </div>
    <div className="sd-d-code-body">{code}</div>
  </div>
);

const UniqueParameterTable = ({ method, lang }) => {
  // Define parameter names based on language
  const p = lang === 'node' 
    ? { id: 'id', user: 'userId', path: 'savePath', ver: 'version', inst: 'instanceId' }
    : { id: 'dataset_id', user: 'user_id', path: 'save_path', ver: 'version', inst: 'instance_id' };

  return (
    <div className="sd-d-table-container">
      <table className="sd-d-param-table">
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Type</th>
            <th>Required</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {/* All methods use ID, USER, PATH */}
          <tr>
            <td className="sd-d-inline-code">{p.id}</td>
            <td>String</td>
            <td className="sd-d-req-yes">Yes</td>
            <td>The unique identifier for the target dataset.</td>
          </tr>
          <tr>
            <td className="sd-d-inline-code">{p.user}</td>
            <td>String</td>
            <td className="sd-d-req-yes">Yes</td>
            <td>Unique identifier for the user requesting the data.</td>
          </tr>
          <tr>
            <td className="sd-d-inline-code">{p.path}</td>
            <td>String</td>
            <td className="sd-d-req-yes">Yes</td>
            <td>Local file system path where files will be saved.</td>
          </tr>

          {/* Version Methods Only */}
          {(method === 'version' || method === 'instance') && (
            <tr>
              <td className="sd-d-inline-code">{p.ver}</td>
              <td>String</td>
              <td className="sd-d-req-yes">Yes</td>
              <td>Semantic version tag (e.g., "1.0.0").</td>
            </tr>
          )}

          {/* Instance Method Only */}
          {method === 'instance' && (
            <tr>
              <td className="sd-d-inline-code">{p.inst}</td>
              <td>String</td>
              <td className="sd-d-req-yes">Yes</td>
              <td>Unique ID for the specific instance run.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SetaDocs;